import React from 'react';
import { Tab, UncontrolledTabs } from 'components/uncontrolled_tabs';
import Model, { useCustomTabs } from 'components/objects/model';
import PropertyList from 'components/property_list';
import { ISerialNumber } from 'interfaces';
import Translation from 'utils/Language/Translation';
import Bool from 'components/Bool';
import Date, { DATE } from 'components/Date';
import { ButtonGroup } from 'reactstrap';
import ProcessingButton from 'components/ProcessingButton';
import { RiNumber1 } from 'react-icons/ri';
import { askPrinterIfNotSet, prompt } from 'utils/Prompts';
import { useApi, useDeviceConfig } from 'utils/API';
import { sleep } from 'component_utils/utils';
import { FaCopy } from 'react-icons/fa';
import moment from 'moment';
import RoleProtected from 'components/RoleProtected';

interface Props {
  inline?: boolean;
  obj: ISerialNumber | null;
}

const Serial = ({ obj: serial, inline }: Props) => {
  const api = useApi();
  const deviceConfig = useDeviceConfig().deviceConfig

  const printLabels = async (quantity: number) => {
    if (quantity) {
      const item = await api.item.getItem(serial.itemCode)
      await api.item.printLabel(
        await askPrinterIfNotSet(
          api,
          <Translation name="T.prompts.printerPicker.uses.serialLabels" />,
          deviceConfig.defaultItemLabelPrinter,
          'defaultItemLabelPrinter'
        ),
        {
          receiptCode: null,
          picklistCode: null,
          deliveryCode: null,
          jobs: [{
            item,
            batch: null,
            serial,
            quantity
          }]
        }
      );
    }
  }

  const shortDescription = () => {
    const t = <Translation name="T.objects.serial.shortDescription" params={(serial as any) || {}} />
    if (serial?.expirationDate) {
      if (moment(serial.expirationDate).isBefore(moment())) {
        return <span className='text-danger'>{t}</span>
      }
    }
    return t    
  };

  const customTabs = useCustomTabs('serials', serial?.code)

  const detailedRender = () => {  
    return (
      <UncontrolledTabs key={serial!.code}>
        <Tab name={<Translation name="T.misc.information" />}>
          <PropertyList
            rows={[
              [
                <b>
                  <Translation name="T.objects.serial.lotCode" />
                </b>,
                serial!.lotCode,
              ],
              [
                <b>
                  <Translation name="T.objects.serial.serialCode" />
                </b>,
                serial!.code,
              ],
              [
                <b>
                  <Translation name="T.objects.serial.serialVisualCode" />
                </b>,
                <>
                  {serial!.visualCode}
                  {' '}
                  <RoleProtected roles={['authCopyObjectCodes']}>
                    <ProcessingButton color="link" className='p-0' onClick={async () => {
                      navigator.clipboard.writeText(serial!.visualCode)
                      await sleep(100)
                    }}>
                      <FaCopy/>
                    </ProcessingButton>
                  </RoleProtected>
                </>,
              ],
              [
                <b>
                  <Translation name="T.objects.serial.mnfLotCode" />
                </b>,
                serial!.mnfLotCode,
              ],
              [
                <b>
                  <Translation name="T.objects.serial.mnfSerialCode" />
                </b>,
                serial!.mnfSerialCode,
              ],
              [
                <b>
                  <Translation name="T.objects.serial.internalSerialCode" />
                </b>,
                serial!.internalSerialCode,
              ],
              [
                <b>
                  <Translation name="T.objects.serial.admissionDate" />
                </b>,
                <Date val={serial.admissionDate} format={DATE} />,
              ],
              [
                <b>
                  <Translation name="T.objects.serial.manufacturingDate" />
                </b>,
                <Date val={serial.manufacturingDate} format={DATE} />,
              ],
              [
                <b><Translation name="T.objects.serial.expirationDate" /></b>,
                <Date val={serial.expirationDate} format={DATE} />,
              ],
              [
                <b><Translation name="T.objects.serial.qualityControlApprovedOrNotRequired" /></b>,
                <Bool val={serial.qualityControlApprovedOrNotRequired} type='YN' />,
              ],
            ]}
          />
        </Tab>
        <Tab name={<Translation name="T.misc.actions" />}>
          <ButtonGroup className="w-100">
            <ProcessingButton
              block
              color="primary"
              onClick={async () => await printLabels(
                parseInt(await prompt(<Translation name="T.misc.howManyLabels" />, ``))
              )}
            >
              <Translation name="T.misc.printLabels" />
            </ProcessingButton>
            <ProcessingButton
              color="info"
              onClick={async () => await printLabels(1)}
            >
              <RiNumber1/>
            </ProcessingButton>
          </ButtonGroup>
        </Tab>
        {customTabs}
      </UncontrolledTabs>
    );
  };

  const dialog = {
    titleText: <Translation name="T.objects.serial.serialDetails" />,
    closeBtnText: <Translation name="T.misc.close" />,
    content: () => <Serial obj={serial} />,
  };
  return (
    <Model
      inline={inline}
      hasLoaded={serial}
      copyValue={() => serial.visualCode}
      description={shortDescription}
      detailedRender={detailedRender}
      dialog={dialog}
    />
  );
};

export default Serial;
